import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { createLead } from '../../services/leadApi';
import { trackEvent } from '../../utils/analytics';
import './ContactPage.css';

/**
 * ContactPage — dedicated /contact page. Renders inside MarketingLayout
 * (logged-out) or GlobalLayout (logged-in) via MarketingRoute.
 *
 * Aug 2 rework (conversion pass): the form previously submitted via mailto:,
 * which silently does nothing for the majority of visitors with no desktop
 * mail client configured — a dead end on a lead-capture surface. It now
 * submits to OUR internal leads API (same path as RoleContactModal: leads in
 * Mongo, /admin/leads, admin email notification), with a honeypot and a GA4
 * `generate_lead` Key Event on success.
 *
 * Oct 12 2026 (Nitish): B2B only. The page is the title, one line for companies
 * bringing on AI employees, and the form. No email address anywhere on the page
 * (our addresses draw far more junk than the form; the form is the only channel
 * that has brought buyers), no trust rows, no self-serve line. Contact left the
 * header the same day; the footer, Pricing's Contact Sales and in-app support
 * links still land here. Events: `form_start`, `form_error`, `generate_lead`.
 */
const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '', botcheck: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const formStarted = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // form_start: fires once per page view, on first field focus — gives us the
  // page_view → form_start → generate_lead funnel (leak between the last two
  // is the form itself).
  const markFormStart = () => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackEvent('form_start', { page: 'contact' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await createLead({
        name: form.name,
        email: form.email,
        message: form.message,
        role: 'General contact',
        source_page: '/contact',
        botcheck: form.botcheck || undefined, // backend silently drops if filled
      });
      if (res.data?.success) {
        trackEvent('generate_lead', { role: 'General contact', source: 'contact_page' });
        setStatus('sent');
      } else {
        trackEvent('form_error', { page: 'contact' });
        setStatus('error');
      }
    } catch (err) {
      trackEvent('form_error', { page: 'contact' });
      setStatus('error');
    }
  };

  const description =
    'Hiring one AI employee for your company, or a whole team of them? Tell us what you need and we\u2019ll help you set it up. You\u2019ll hear back within 1 business day.';

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact CellCog',
    url: `${SEO_DEFAULTS.url}/contact`,
    mainEntity: {
      '@type': 'Organization',
      name: 'CellCog',
      url: SEO_DEFAULTS.url,
      contactPoint: [
        {
          '@type': 'ContactPoint',
          url: `${SEO_DEFAULTS.url}/contact`,
          contactType: 'sales',
          availableLanguage: 'English',
        },
      ],
    },
  };

  return (
    <>
      <Helmet>
        <title>Contact — CellCog</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/contact`} />
        <link rel="alternate" type="text/markdown" href={`${SEO_DEFAULTS.url}/contact/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact — CellCog" />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/contact`} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/contact-og.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact — CellCog" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/contact-og.png`} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      <div className="mkt-contact">
        <div className="mkt-contact__inner">
          <div className="mkt-contact__intro">
            <h1 className="mkt-contact__title mkt-contact__o1">Get in touch</h1>
            <p className="mkt-contact__sub mkt-contact__o2">
              Hiring one AI employee for your company, or a whole team of
              them? Tell us what you need and we&rsquo;ll help you set it up.
              You&rsquo;ll hear back from us within 1 business day.
            </p>
          </div>

          {status === 'sent' ? (
            <div className="mkt-contact__form mkt-contact__o3 mkt-contact__thanks">
              <span className="mkt-contact__tick"><CheckOutlined /></span>
              <h2>Message sent</h2>
              <p>Thanks for reaching out. We&rsquo;ll get back to you within 1 business day.</p>
            </div>
          ) : (
            <form className="mkt-contact__form mkt-contact__o3" onSubmit={submit}>
              <input
                className="mkt-contact__hp"
                type="text"
                value={form.botcheck}
                onChange={set('botcheck')}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <label className="mkt-contact__field">
                <span>Name</span>
                <input type="text" required value={form.name} onChange={set('name')} onFocus={markFormStart} placeholder="Your name" />
              </label>
              <label className="mkt-contact__field">
                <span>Email</span>
                <input type="email" required value={form.email} onChange={set('email')} onFocus={markFormStart} placeholder="you@company.com" />
              </label>
              <label className="mkt-contact__field">
                <span>Message</span>
                <textarea rows={5} required value={form.message} onChange={set('message')} onFocus={markFormStart} placeholder="How can we help?" />
              </label>
              <button type="submit" className="mkt-contact__submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send message'} <ArrowRightOutlined />
              </button>
              {status === 'error' && (
                <p className="mkt-contact__error">Something went wrong. Please try again.</p>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactPage;
